import type React from "react"
import { Link } from "react-router-dom"
import Navbar from "../../components/Navbar"
import Footer from "../../components/Footer"
import Image from "../../components/Image"
import {
  CheckCircle,
  Building,
  GraduationCap,
  Factory,
  ShoppingCart,
  Calendar,
  DollarSign,
  Users,
  Shield,
} from "lucide-react"

const FacilityManagementPage: React.FC = () => {
  const facilityTypes = [
    {
      title: "Educational Institutions",
      description: "Comprehensive maintenance solutions for universities, schools, and educational campuses",
      icon: GraduationCap,
      image: "https://placehold.co/600x400?text=Educational+Campus",
      applications: [
        "Campus grounds maintenance",
        "Sports field care",
        "Parking lot cleaning",
        "Walkway maintenance",
        "Seasonal operations",
        "Emergency cleanup",
      ],
      attachments: [
        "Cylinder Mower",
        "Street Sweeper",
        "Leaf Blower",
        "Snow Plough",
        "Salt Spreader",
        "Pressure Washer",
      ],
      benefits: [
        "Enhanced campus appearance",
        "Safe learning environment",
        "Cost-effective operations",
        "Year-round maintenance capability",
      ],
    },
    {
      title: "Corporate Campuses",
      description: "Professional maintenance for corporate headquarters and business complexes",
      icon: Building,
      image: "https://placehold.co/600x400?text=Corporate+Campus",
      applications: [
        "Landscaping maintenance",
        "Parking facility cleaning",
        "Entrance area upkeep",
        "Outdoor space management",
        "Winter maintenance",
        "Event preparation",
      ],
      attachments: [
        "Rotary Mower",
        "Surface Cleaner",
        "High Volume Sweeper",
        "Watering Arm",
        "De-icing Sprayer",
        "High Tip Bucket",
      ],
      benefits: [
        "Professional corporate image",
        "Employee satisfaction",
        "Visitor impression management",
        "Property value maintenance",
      ],
    },
    {
      title: "Industrial Facilities",
      description: "Heavy-duty maintenance solutions for manufacturing and industrial complexes",
      icon: Factory,
      image: "https://placehold.co/600x400?text=Industrial+Facility",
      applications: [
        "Heavy-duty cleaning",
        "Loading dock maintenance",
        "Perimeter security upkeep",
        "Equipment area cleaning",
        "Waste management support",
        "Safety compliance maintenance",
      ],
      attachments: [
        "Heavy Duty Sweeper",
        "Pressure Washer",
        "High Tip Bucket",
        "Surface Cleaner",
        "Debris Collector",
        "Safety Equipment",
      ],
      benefits: ["Operational efficiency", "Safety compliance", "Equipment longevity", "Regulatory adherence"],
    },
    {
      title: "Retail & Commercial Centers",
      description: "Maintaining attractive and safe environments for shopping centers and retail complexes",
      icon: ShoppingCart,
      image: "https://placehold.co/600x400?text=Shopping+Center",
      applications: [
        "Parking lot maintenance",
        "Entrance cleaning",
        "Landscaping care",
        "Seasonal decorations support",
        "Event space preparation",
        "Emergency response",
      ],
      attachments: [
        "Street Cleaner",
        "Leaf Collector",
        "Pressure Washer",
        "Snow Removal",
        "Landscaping Tools",
        "Event Support Equipment",
      ],
      benefits: [
        "Customer attraction",
        "Shopping experience enhancement",
        "Tenant satisfaction",
        "Revenue optimization",
      ],
    },
  ]

  const maintenanceServices = [
    {
      category: "Grounds Maintenance",
      services: [
        "Lawn mowing and edging",
        "Landscape maintenance",
        "Tree and shrub care",
        "Irrigation system support",
        "Seasonal planting assistance",
        "Pest control support",
      ],
    },
    {
      category: "Cleaning Services",
      services: [
        "Parking lot sweeping",
        "Walkway cleaning",
        "Building exterior washing",
        "Entrance area maintenance",
        "Debris removal",
        "Pressure washing services",
      ],
    },
    {
      category: "Seasonal Operations",
      services: [
        "Snow removal and plowing",
        "Ice management and de-icing",
        "Leaf collection and removal",
        "Storm cleanup",
        "Winter preparation",
        "Spring cleanup services",
      ],
    },
    {
      category: "Specialized Services",
      services: [
        "Event setup and cleanup",
        "Emergency response",
        "Construction support",
        "Moving and logistics support",
        "Waste management assistance",
        "Security perimeter maintenance",
      ],
    },
  ]

  const facilityBenefits = [
    {
      icon: DollarSign,
      title: "Cost Efficiency",
      description: "Reduce operational costs with versatile equipment that handles multiple maintenance tasks",
      stats: "Up to 40% cost reduction vs. multiple specialized vehicles",
    },
    {
      icon: Calendar,
      title: "Year-Round Operations",
      description: "Single equipment solution for all seasons and weather conditions",
      stats: "365 days per year operational capability",
    },
    {
      icon: Users,
      title: "Simplified Management",
      description: "Streamlined operations with one machine, one operator, multiple capabilities",
      stats: "Reduced training and certification requirements",
    },
    {
      icon: Shield,
      title: "Enhanced Safety",
      description: "Professional maintenance ensures safe environments for employees and visitors",
      stats: "Improved safety compliance and reduced liability",
    },
  ]

  const caseStudies = [
    {
      facility: "Melbourne University",
      type: "Educational Institution",
      size: "150 hectares, 50,000 students",
      challenge:
        "Needed efficient solution for campus-wide maintenance including sports fields, walkways, and parking areas while managing tight budget constraints.",
      solution: "Multihog MX with comprehensive attachment package including mowers, sweepers, and winter equipment",
      implementation: [
        "Replaced 5 different specialized vehicles",
        "Trained 3 operators on multi-function equipment",
        "Established year-round maintenance schedule",
        "Integrated with existing facility management systems",
      ],
      results: [
        "45% reduction in equipment maintenance costs",
        "30% improvement in response time for maintenance requests",
        "Enhanced campus appearance and safety ratings",
        "Simplified operator training and certification process",
        "Improved student and staff satisfaction scores",
      ],
      quote:
        "The Multihog system has revolutionized our campus maintenance operations. We can now respond to any maintenance need quickly and efficiently, regardless of the season.",
      spokesperson: "David Chen, Facilities Manager",
    },
    {
      facility: "TechCorp Business Park",
      type: "Corporate Campus",
      size: "25 buildings, 5,000 employees",
      challenge:
        "Required professional maintenance solution that could maintain corporate image while handling diverse facility management needs across multiple buildings.",
      solution: "Multihog CX with specialized corporate campus attachment package",
      implementation: [
        "Customized maintenance schedules for each building",
        "Integrated with property management software",
        "Established emergency response protocols",
        "Coordinated with landscaping and security teams",
      ],
      results: [
        "Improved corporate campus appearance ratings by 35%",
        "Reduced facility maintenance complaints by 60%",
        "Enhanced employee satisfaction with work environment",
        "Streamlined vendor management and reduced costs",
        "Achieved sustainability goals through efficient operations",
      ],
      quote:
        "Our corporate campus has never looked better. The versatility of the Multihog allows us to maintain professional standards year-round while staying within budget.",
      spokesperson: "Sarah Williams, Property Manager",
    },
  ]

  const attachmentCategories = [
    {
      category: "Grounds Care",
      attachments: [
        { name: "Cylinder Mower", use: "Precision cutting for sports fields and fine turf areas" },
        { name: "Rotary Mower", use: "General purpose mowing for large grass areas" },
        { name: "Flail Mower", use: "Heavy-duty cutting for rough terrain and overgrown areas" },
        { name: "Watering Arm", use: "Irrigation support and precision watering" },
      ],
    },
    {
      category: "Cleaning & Maintenance",
      attachments: [
        { name: "Street Sweeper", use: "Parking lots, walkways, and paved surface cleaning" },
        { name: "Pressure Washer", use: "Building exteriors, concrete cleaning, and deep cleaning" },
        { name: "Leaf Blower", use: "Debris removal and seasonal cleanup operations" },
        { name: "Surface Cleaner", use: "Specialized surface cleaning and maintenance" },
      ],
    },
    {
      category: "Winter Operations",
      attachments: [
        { name: "Snow Plough", use: "Parking lot and walkway snow removal" },
        { name: "Salt Spreader", use: "Ice prevention and traction improvement" },
        { name: "De-icing Sprayer", use: "Liquid de-icing for sensitive areas" },
        { name: "Snow Brush", use: "Gentle snow removal for delicate surfaces" },
      ],
    },
    {
      category: "Specialized Equipment",
      attachments: [
        { name: "High Tip Bucket", use: "Material handling and waste management support" },
        { name: "Debris Collector", use: "Large debris and waste collection" },
        { name: "Event Support Kit", use: "Setup and breakdown assistance for events" },
        { name: "Emergency Response Kit", use: "Rapid response for facility emergencies" },
      ],
    },
  ]

  const maintenanceSchedule = [
    {
      season: "Spring",
      tasks: [
        "Deep cleaning after winter",
        "Landscape preparation",
        "Equipment inspection",
        "Irrigation system startup",
        "Parking lot maintenance",
        "Building exterior cleaning",
      ],
    },
    {
      season: "Summer",
      tasks: [
        "Regular mowing and trimming",
        "Irrigation management",
        "Event support services",
        "Parking lot sweeping",
        "Outdoor area maintenance",
        "Heat stress prevention",
      ],
    },
    {
      season: "Fall",
      tasks: [
        "Leaf collection and removal",
        "Winter preparation",
        "Drainage system maintenance",
        "Landscape winterization",
        "Equipment winterization",
        "Storm preparation",
      ],
    },
    {
      season: "Winter",
      tasks: [
        "Snow removal operations",
        "Ice management",
        "Emergency response",
        "Indoor area support",
        "Equipment maintenance",
        "Safety compliance checks",
      ],
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Facility Management
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional
              <br />
              <span className="text-purple-400">Facility Management</span>
              <br />
              Solutions
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Comprehensive maintenance solutions for educational institutions, corporate campuses, industrial
              facilities, and commercial complexes. Maintain professional standards year-round with versatile,
              cost-effective equipment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                Get Facility Solutions Quote
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-purple-900 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                View Facility-Ready Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Types Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Facility Management Applications</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Specialized solutions for diverse facility types, each with unique maintenance requirements and
                operational challenges.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {facilityTypes.map((facility, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <Image
                    src={facility.image || "/placeholder.svg"}
                    alt={facility.title}
                    className="w-full h-48 object-cover"
                    width={600}
                    height={400}
                  />
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-purple-100 rounded-full p-3 mr-4">
                        <facility.icon className="h-6 w-6 text-purple-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{facility.title}</h3>
                    </div>

                    <p className="text-gray-600 mb-4 leading-relaxed">{facility.description}</p>

                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Applications:</h4>
                      <div className="grid grid-cols-2 gap-1">
                        {facility.applications.map((app, idx) => (
                          <div key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="h-3 w-3 text-purple-600 mr-2 flex-shrink-0" />
                            {app}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Recommended Attachments:</h4>
                      <div className="flex flex-wrap gap-1">
                        {facility.attachments.map((attachment, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs"
                          >
                            {attachment}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Benefits:</h4>
                      <div className="space-y-1">
                        {facility.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="h-3 w-3 text-purple-600 mr-2 flex-shrink-0" />
                            {benefit}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Multihog for Facility Management?</h2>
              <p className="text-lg text-gray-600">
                Experience the advantages that make Multihog the preferred choice for professional facility management
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {facilityBenefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center">
                  <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <benefit.icon className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 mb-3">{benefit.description}</p>
                  <p className="text-purple-600 font-semibold text-sm">{benefit.stats}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Comprehensive Maintenance Services</h2>
              <p className="text-lg text-gray-600">
                Full-service facility maintenance capabilities with specialized Multihog attachments
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {maintenanceServices.map((service, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">{service.category}</h3>
                  <ul className="space-y-2">
                    {service.services.map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal Maintenance Schedule */}
      <section className="py-20 bg-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Year-Round Maintenance Schedule</h2>
              <p className="text-lg text-gray-600">
                Comprehensive maintenance planning for all seasons and facility needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {maintenanceSchedule.map((season, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-xl font-bold text-purple-600 mb-4">{season.season}</h3>
                  <ul className="space-y-2">
                    {season.tasks.map((task, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                        {task}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Attachment Categories */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Specialized Facility Management Attachments</h2>
              <p className="text-lg text-gray-600">
                Purpose-built attachments for comprehensive facility maintenance operations
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {attachmentCategories.map((category, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">{category.category}</h3>
                  <div className="space-y-4">
                    {category.attachments.map((attachment, idx) => (
                      <div key={idx} className="border-l-4 border-purple-500 pl-4">
                        <h4 className="font-semibold text-gray-900">{attachment.name}</h4>
                        <p className="text-gray-600 text-sm">{attachment.use}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Facility Management Success Stories</h2>
              <p className="text-lg text-gray-600">
                Real results from facilities that have transformed their operations with Multihog
              </p>
            </div>

            <div className="space-y-12">
              {caseStudies.map((study, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="grid lg:grid-cols-3 gap-8 p-8">
                    <div>
                      <div className="flex items-center mb-4">
                        <Building className="h-8 w-8 text-purple-600 mr-3" />
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{study.facility}</h3>
                          <p className="text-purple-600 font-medium">{study.type}</p>
                          <p className="text-gray-600 text-sm">{study.size}</p>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-2">Challenge:</h4>
                        <p className="text-gray-600 text-sm">{study.challenge}</p>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Solution:</h4>
                        <p className="text-purple-600 font-medium text-sm">{study.solution}</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Implementation:</h4>
                      <ul className="space-y-2 mb-6">
                        {study.implementation.map((item, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Results:</h4>
                      <ul className="space-y-2 mb-6">
                        {study.results.map((result, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                            {result}
                          </li>
                        ))}
                      </ul>

                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-gray-700 italic text-sm mb-2">"{study.quote}"</p>
                        <p className="text-purple-600 font-medium text-sm">— {study.spokesperson}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Facility Management ROI</h2>
              <p className="text-lg text-gray-600">
                See how Multihog solutions deliver measurable value for facility management operations
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Cost Savings Analysis</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                      <span className="text-gray-700">Equipment Consolidation</span>
                      <span className="font-semibold text-purple-600">$85,000/year</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                      <span className="text-gray-700">Reduced Labor Costs</span>
                      <span className="font-semibold text-purple-600">$45,000/year</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                      <span className="text-gray-700">Maintenance Efficiency</span>
                      <span className="font-semibold text-purple-600">$35,000/year</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                      <span className="text-gray-700">Operational Efficiency</span>
                      <span className="font-semibold text-purple-600">$25,000/year</span>
                    </div>
                    <div className="flex justify-between items-center font-bold text-lg pt-2">
                      <span>Total Annual Savings</span>
                      <span className="text-purple-600">$190,000</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Performance Improvements</h3>
                  <div className="space-y-4">
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600 mb-1">40%</div>
                      <div className="text-gray-700">Faster Response Times</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600 mb-1">60%</div>
                      <div className="text-gray-700">Reduced Maintenance Complaints</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600 mb-1">35%</div>
                      <div className="text-gray-700">Improved Facility Appearance</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600 mb-1">18 months</div>
                      <div className="text-gray-700">Typical ROI Payback Period</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/contact"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-block"
                >
                  Get Custom ROI Analysis
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Transform Your Facility Management</h2>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Join the growing number of facilities worldwide that trust Multihog for comprehensive, cost-effective
              maintenance solutions. Improve efficiency, reduce costs, and enhance your facility's professional
              appearance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                Schedule Facility Consultation
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-purple-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                View Facility-Ready Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default FacilityManagementPage
